import { VStack, Text, Button, HStack, Link } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import MfaEnterCode from './MfaEnterCode';
import { useContext } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { useMfaApi } from '../../../hooks/useMfaApi';
import { useMfaToast } from '../../../hooks/useMfaToast';
import { ArrowBackIcon } from '@chakra-ui/icons';

function MfaVerifications() {
  const { authState, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { sendEmailCode } = useMfaApi();
  const { showErrorToast } = useMfaToast();
  const hasSentEmail = useRef(false);
  const isEmail = authState.account?.otp_method === 'email';
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [useAnotherMethod, setUseAnotherMethod] = useState(false);

  const handleBack = () => {
    try {
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      showErrorToast('Error logging out', 'Please try again');
    }
  };

  const sendEmailCodeHandler = async () => {
    try {
      await sendEmailCode();
    } catch (error) {
      showErrorToast(
        'Error sending verification code email',
        error.response?.data?.message || 'Failed to send verification code'
      );
    }
  };

  const commonTextStyles = {
    textStyle: 'bodySmall',
    color: '#666666',
  };

  const linkTextStyles = {
    ...commonTextStyles,
    textDecoration: 'underline',
    cursor: 'pointer',
    _hover: { color: 'orangePrimary' },
  };

  const ContactSupportLink = () => (
    <Link href="mailto:support@passentry.com" {...linkTextStyles}>
      Contact support
    </Link>
  );

  const ClickableText = ({ children, onClick }) => (
    <Text {...linkTextStyles} onClick={onClick}>
      {children}
    </Text>
  );

  const HelpSection = ({ questionText, actionElement, style = {} }) => (
    <HStack {...style}>
      <Text {...commonTextStyles}>{questionText}</Text>
      {actionElement}
    </HStack>
  );

  const handleUseAnotherMethod = () => {
    setUseAnotherMethod(true);
    setUseRecoveryCode(false);
    sendEmailCodeHandler();
  };

  useEffect(() => {
    if (isEmail && !hasSentEmail.current) {
      hasSentEmail.current = true;
      sendEmailCodeHandler();
    }
  }, [isEmail]);

  return (
    <VStack alignItems="flex-start">
      <HStack w="full" justifyContent="space-between" alignItems="center">
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          onClick={handleBack}
          size="sm"
          p={0}
        >
          Back
        </Button>
      </HStack>
      <VStack mb={4} alignItems="flex-start">
        <Text textStyle="heading2">Verify it's you</Text>
        <Text textStyle="bodySmall" color="#666666">
          {useRecoveryCode
            ? 'Enter your recovery code below to finish signing in.'
            : 'Enter your verification code below to finish signing in.'}
        </Text>
      </VStack>
      <MfaEnterCode
        isEmail={isEmail}
        isRecoveryCode={useRecoveryCode}
        useAnotherMethod={useAnotherMethod}
      />

      {!useRecoveryCode && !useAnotherMethod && (
        <HelpSection
          style={isEmail ? {} : { mt: 4 }}
          questionText="Can't access your verification method?"
          actionElement={
            isEmail ? (
              <ContactSupportLink />
            ) : (
              <ClickableText onClick={() => setUseRecoveryCode(true)}>
                Use Recovery Code
              </ClickableText>
            )
          }
        />
      )}

      {useRecoveryCode && (
        <HelpSection
          style={{ mt: 4 }}
          questionText="Don't have your recovery code?"
          actionElement={
            <ClickableText onClick={handleUseAnotherMethod}>
              Try different method
            </ClickableText>
          }
        />
      )}

      {useAnotherMethod && (
        <HelpSection
          questionText="Still having trouble?"
          actionElement={<ContactSupportLink />}
        />
      )}
    </VStack>
  );
}

export default MfaVerifications;
